import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    CheckCircle2,
    Circle,
    CalendarClock,
    AlertCircle,
    Target,
    Quote,
    BellRing,
    ArrowRight,
    ListChecks,
    TrendingUp,
} from "lucide-react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { nyNow, nyGreeting, nyFormat } from "@/lib/time";

export default function Dashboard() {
    const { user } = useAuth();
    const [summary, setSummary] = useState(null);
    const [verse, setVerse] = useState(null);
    const [routine, setRoutine] = useState([]);
    const [checks, setChecks] = useState({});
    const [reminders, setReminders] = useState([]);
    const [, setTick] = useState(0);

    useEffect(() => {
        const t = setInterval(() => setTick((v) => v + 1), 30000);
        return () => clearInterval(t);
    }, []);

    const load = async () => {
        const [s, v, r, c, rm] = await Promise.all([
            api.get("/dashboard/summary"),
            api.get("/scripture/today"),
            api.get("/routine-items"),
            api.get("/checklist/today"),
            api.get("/reminders"),
        ]);
        setSummary(s.data);
        setVerse(v.data);
        setRoutine(r.data.sort((a, b) => (a.order || 0) - (b.order || 0)));
        const m = {};
        c.data.forEach((x) => (m[x.routine_item_id] = x.checked));
        setChecks(m);
        setReminders(rm.data);
    };

    useEffect(() => {
        load();
    }, []);

    const toggle = async (id) => {
        const next = !checks[id];
        setChecks({ ...checks, [id]: next });
        await api.post("/checklist/toggle", { routine_item_id: id, checked: next });
        const s = await api.get("/dashboard/summary");
        setSummary(s.data);
    };

    const { date, time } = nyNow();
    const upcoming = reminders.filter((r) => new Date(r.scheduled_at) > new Date()).sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at));
    const firstName = user?.name || (user?.email ? user.email.split("@")[0] : "");

    return (
        <div className="space-y-8" data-testid="dashboard-page">
            {/* Welcoming greeting */}
            <section className="twp-card p-6 md:p-8" data-testid="greeting-card">
                <div className="flex items-start justify-between gap-6 flex-wrap">
                    <div>
                        <div className="caption">{date} · <span className="font-mono text-[#D4AF37]">{time}</span> ET</div>
                        <h1 className="font-serif text-4xl sm:text-5xl mt-3">
                            {nyGreeting()},{" "}
                            <span className="gold capitalize">{firstName}</span>.
                        </h1>
                        <p className="mt-3 text-zinc-400 max-w-xl leading-relaxed">
                            Prepare slowly. Trade your plan. Trust the process. The day is already His — your part is to be
                            faithful with it.
                        </p>
                    </div>
                    <div className="flex-shrink-0 min-w-[180px]">
                        <div className="caption">Prep progress</div>
                        <div className="mt-2 flex items-baseline gap-2">
                            <div className="font-serif text-5xl gold">{summary?.prep_progress ?? 0}%</div>
                        </div>
                        <div className="mt-2 h-1.5 bg-white/5 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-[#D4AF37] transition-all duration-500"
                                style={{ width: `${summary?.prep_progress ?? 0}%` }}
                            />
                        </div>
                        <div className="caption mt-2">
                            {summary?.routine_checked ?? 0} / {summary?.routine_total ?? 0} items checked
                        </div>
                    </div>
                </div>
            </section>

            {/* Bento grid */}
            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Prep checklist */}
                <div className="twp-card p-6 lg:col-span-1" data-testid="prep-checklist">
                    <div className="flex items-center gap-2 caption">
                        <ListChecks className="w-4 h-4 gold" strokeWidth={1.5} /> Trading prep
                    </div>
                    <h3 className="font-serif text-xl mt-2">Today's routine</h3>
                    <div className="mt-5 space-y-2 max-h-72 overflow-y-auto">
                        {routine.length === 0 && (
                            <div className="text-sm text-zinc-500">
                                No routine items yet.{" "}
                                <Link to="/trading" className="text-[#D4AF37] hover:underline">
                                    Set up your routine →
                                </Link>
                            </div>
                        )}
                        {routine.map((r) => {
                            const done = !!checks[r.id];
                            return (
                                <button
                                    key={r.id}
                                    onClick={() => toggle(r.id)}
                                    data-testid={`toggle-${r.id}`}
                                    className={`w-full text-left flex items-start gap-3 px-3 py-2 rounded-md border transition-colors ${
                                        done ? "border-[#5B8A62]/30 bg-[#5B8A62]/5" : "border-white/5 hover:border-white/10"
                                    }`}
                                >
                                    {done ? (
                                        <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#5B8A62]" strokeWidth={1.5} />
                                    ) : (
                                        <Circle className="w-4 h-4 mt-0.5 text-zinc-500" strokeWidth={1.5} />
                                    )}
                                    <span className={`text-sm ${done ? "text-zinc-400 line-through" : ""}`}>{r.title}</span>
                                    <span className="ml-auto text-[10px] text-zinc-500 uppercase tracking-wider">
                                        {r.phase?.replace("_", " ")}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Next session / reminder */}
                <div className="twp-card p-6" data-testid="next-session-card">
                    <div className="flex items-center gap-2 caption">
                        <BellRing className="w-4 h-4 gold" strokeWidth={1.5} /> Next up
                    </div>
                    <h3 className="font-serif text-xl mt-2">Session / Reminder</h3>
                    {summary?.next_reminder ? (
                        <div className="mt-4">
                            <div className="text-sm text-zinc-400 uppercase tracking-wider">
                                {summary.next_reminder.kind.replace("_", " ")}
                            </div>
                            <div className="font-serif text-xl mt-1">{summary.next_reminder.title}</div>
                            <div className="mt-2 flex items-center gap-2 text-sm text-zinc-500">
                                <CalendarClock className="w-4 h-4" strokeWidth={1.5} />
                                {nyFormat(summary.next_reminder.scheduled_at)} ET
                            </div>
                        </div>
                    ) : (
                        <div className="mt-4 text-sm text-zinc-500">
                            Nothing scheduled.{" "}
                            <Link to="/reminders" className="text-[#D4AF37] hover:underline">
                                Add a reminder →
                            </Link>
                        </div>
                    )}
                    {upcoming.length > 1 && (
                        <div className="mt-5 pt-4 border-t border-white/5">
                            <div className="caption mb-2">Then</div>
                            <div className="space-y-1">
                                {upcoming.slice(1, 4).map((r) => (
                                    <div key={r.id} className="text-xs text-zinc-500 flex justify-between gap-3">
                                        <span className="truncate">{r.title}</span>
                                        <span className="font-mono text-[11px]">{nyFormat(r.scheduled_at)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* High-impact events */}
                <div className="twp-card p-6" data-testid="events-card">
                    <div className="flex items-center gap-2 caption">
                        <AlertCircle className="w-4 h-4 gold" strokeWidth={1.5} /> High-impact · US
                    </div>
                    <h3 className="font-serif text-xl mt-2">Economic events</h3>
                    <div className="mt-4 space-y-3">
                        {(summary?.upcoming_events || []).length === 0 && (
                            <div className="text-sm text-zinc-500">
                                No upcoming events.{" "}
                                <Link to="/calendar" className="text-[#D4AF37] hover:underline">
                                    Add or load samples →
                                </Link>
                            </div>
                        )}
                        {(summary?.upcoming_events || []).map((e) => (
                            <div key={e.id} className="flex items-start justify-between gap-4">
                                <div>
                                    <div className="text-sm flex items-center gap-2">
                                        {e.event}
                                        {e.is_sample && (
                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30 uppercase tracking-wider">
                                                Sample
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-zinc-500 mt-0.5">
                                        {nyFormat(e.scheduled_at)} ET · {e.country}
                                    </div>
                                </div>
                                <span className="text-[10px] uppercase tracking-wider text-[#D4AF37]">{e.importance}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Daily verse - spans 2 cols */}
                <div className="twp-card p-6 md:col-span-2" data-testid="verse-card">
                    <div className="flex items-center gap-2 caption">
                        <Quote className="w-4 h-4 gold" strokeWidth={1.5} /> Daily Scripture · <span className="text-[#D4AF37]">{verse?.translation || "NKJV"}</span>
                    </div>
                    {verse && (
                        <>
                            <blockquote className="font-serif text-xl sm:text-2xl mt-4 leading-relaxed text-zinc-100">
                                &ldquo;{verse.text}&rdquo;
                            </blockquote>
                            <div className="mt-3 text-sm gold font-mono tracking-wide">— {verse.ref}</div>
                            <div className="mt-5 pt-5 border-t border-white/5 grid md:grid-cols-2 gap-5">
                                <div>
                                    <div className="caption">Reflect</div>
                                    <p className="text-sm text-zinc-300 mt-2 leading-relaxed">{verse.reflection_prompt}</p>
                                </div>
                                <div>
                                    <div className="caption">Pray</div>
                                    <p className="text-sm text-zinc-300 mt-2 leading-relaxed italic">{verse.prayer_prompt}</p>
                                </div>
                            </div>
                            <div className="mt-4 text-[11px] text-zinc-600">{verse.disclaimer}</div>
                            <Link
                                to="/faith"
                                className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#D4AF37] hover:underline"
                                data-testid="verse-journal-link"
                            >
                                Journal this reflection <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                        </>
                    )}
                </div>

                {/* Goals + stats */}
                <div className="twp-card p-6" data-testid="goals-card">
                    <div className="flex items-center gap-2 caption">
                        <Target className="w-4 h-4 gold" strokeWidth={1.5} /> Personal goals
                    </div>
                    <h3 className="font-serif text-xl mt-2">In progress</h3>
                    <div className="mt-4 space-y-3">
                        {(summary?.top_goals || []).length === 0 && (
                            <div className="text-sm text-zinc-500">
                                No goals yet.{" "}
                                <Link to="/tasks" className="text-[#D4AF37] hover:underline">
                                    Add a goal →
                                </Link>
                            </div>
                        )}
                        {(summary?.top_goals || []).map((g) => (
                            <div key={g.id}>
                                <div className="flex justify-between text-sm">
                                    <span className="truncate">{g.title}</span>
                                    <span className="text-zinc-500 text-xs">{g.progress}%</span>
                                </div>
                                <div className="h-1 bg-white/5 rounded-full mt-1 overflow-hidden">
                                    <div className="h-full bg-[#D4AF37]" style={{ width: `${g.progress}%` }} />
                                </div>
                                <div className="caption mt-1">{g.category}</div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-5 pt-4 border-t border-white/5 grid grid-cols-3 gap-3">
                        <Stat label="Open tasks" value={`${summary?.open_tasks ?? 0}/${summary?.total_tasks ?? 0}`} />
                        <Stat label="Live trades" value={summary?.live_trade_count ?? 0} />
                        <Stat label="Total R" value={summary?.live_total_r ?? 0} accent />
                    </div>
                </div>
            </section>

            {/* Quick actions */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="quick-actions">
                <QuickAction to="/journal" label="Log a trade" icon={TrendingUp} testid="qa-log-trade" />
                <QuickAction to="/trading" label="Record observation" icon={ListChecks} testid="qa-observation" />
                <QuickAction to="/faith" label="Weekly reflection" icon={Quote} testid="qa-reflection" />
                <QuickAction to="/calendar" label="Add event" icon={CalendarClock} testid="qa-event" />
            </section>
        </div>
    );
}

function Stat({ label, value, accent }) {
    return (
        <div>
            <div className={`font-serif text-2xl ${accent ? "gold" : ""}`}>{value}</div>
            <div className="caption">{label}</div>
        </div>
    );
}

function QuickAction({ to, label, icon: Icon, testid }) {
    return (
        <Link
            to={to}
            data-testid={testid}
            className="twp-card px-4 py-3 flex items-center gap-3 hover:border-[#D4AF37]/40 transition-colors"
        >
            <Icon className="w-4 h-4 gold" strokeWidth={1.5} />
            <span className="text-sm">{label}</span>
            <ArrowRight className="w-3.5 h-3.5 ml-auto text-zinc-600" />
        </Link>
    );
}
